import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, fromPromise } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import packageJson from 'package.alias.json'
import { API_VERSION_HEADER, UNAUTHENTICATED_CODE } from 'constants/consts'
import { authService } from 'utils/services/auth'
import { isApiVersionMatch } from 'utils/isApiVersionMatch'
import { routes } from '../routes/cmsRoutes'

const httpLink = new HttpLink({
  uri: `${process.env.REACT_APP_API_URL}`,
})

const DONT_CHECK_VERSION_WITH_CODES = ['UNAUTHENTICATED']
const DONT_CHECK_VERSION_WITH_OPERATIONS = ['checkVersion']

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const graphQLError of graphQLErrors) {
      const { message, path, extensions } = graphQLError
      console.error(`[GraphQL error]: Message: ${message}, Path: ${path}`)

      if (extensions?.code === UNAUTHENTICATED_CODE) {
        const resolved = fromPromise(authService.refreshToken())

        return resolved.flatMap(() => {
          operation.setContext(async ({ headers = {} }) => {
            const token = await authService.getToken()

            return {
              headers: {
                ...headers,
                authorization: token,
              },
            }
          })

          return forward?.(operation)
        })
      }

      if (
        !DONT_CHECK_VERSION_WITH_CODES.includes(extensions?.code) &&
        !DONT_CHECK_VERSION_WITH_OPERATIONS.includes(operation.operationName)
      ) {
        const context = operation.getContext()
        const {
          response: { headers },
        } = context

        // compare actual and expected API version and show maintenance screen if needed
        const actualApiVersion = headers?.get(API_VERSION_HEADER) ?? ''

        if (
          !isApiVersionMatch(actualApiVersion, packageJson.expectedMinApiVersion, packageJson.expectedMaxApiVersion)
        ) {
          location.replace(routes.MAINTENANCE)
        }
      }
    }
  }

  if (networkError) {
    console.error('[Network error]:', networkError)
  }
})

const getAuthLink = () =>
  setContext(async (_, { headers }) => {
    const token = await authService.getToken()

    if (token) {
      return {
        headers: {
          ...headers,
          authorization: token,
        },
      }
    }
    return {
      headers,
    }
  })

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, getAuthLink(), httpLink]),
  cache: new InMemoryCache(),
})

export default client
